import { ICashsaleFormValues } from "@saleor/createOrder/components/CashSaleForm/types";
import {
  CashSaleInput,
  CipChangeOrderEditInput,
  CipChangeOrderHeaderInput,
  CipChangeOrderItemDetailsInput,
  CipOrderInformationInput,
  CipOrderLineInput,
  CipOrderOrganizationalDataInput,
  Get_Realtime_PartnerMutation,
  GetCashSaleStatusQuery,
  GetOrderDataQuery,
  OrderHeaderPriceInfo,
  OrderLinePriceInfo,
  OrderViewDataQuery,
  ProductionFlag,
  RealtimePartnerType,
  SplitCipOrderLineInput,
  SplitCipOrderLineInputAfterCp,
  SubmitCashSaleMutationVariables,
  SubmitCashSaleUpdateMutationVariables
} from "@saleor/graphql";
import { FormikProps } from "formik";
import isEqual from "lodash/isEqual";
import moment from "moment-timezone";

import { ITEM_STATUS } from "./constants";
import { deriveShipToBillTo, parseNumber } from "./helpers";

export const getRealTimePartnerDataByType = (
  type: RealtimePartnerType,
  dataRealtime: Get_Realtime_PartnerMutation
) => {
  if (type === RealtimePartnerType.SHIP_TO) {
    return dataRealtime?.getSapRealtimePartner?.response?.filter(
      e => e.partnerFunction === "WE"
    );
  }
  if (type === RealtimePartnerType.BILL_TO) {
    return dataRealtime?.getSapRealtimePartner?.response?.filter(
      e => e.partnerFunction === "RE"
    );
  }
  return [];
};

export enum ItemTab {
  tabA = "orderInformation",
  tabC = "additionalData",
  tabD = "cashSale"
}

export enum BATCH_NO {
  NO_PRINT = "NO PRINT"
}

export enum BATCH_FLAG_CHOICES {
  TEXT = "TEXT",
  BLANK = "BLANK"
}

const LOCAL_KEY = "localIds";
export function setLocalOrderId(soldToCode: string, orderId: string) {
  const value = JSON.stringify({ [soldToCode]: orderId });
  localStorage.setItem(LOCAL_KEY, value);
}
export function getLocalOrderId(soldToCode: string): string {
  const local = JSON.parse(localStorage.getItem(LOCAL_KEY));
  if (local?.[soldToCode]) {
    return local[soldToCode];
  }
}
export function clearLocalOrderIds() {
  localStorage.removeItem(LOCAL_KEY);
}
export function deleteLocalOrderId(soldToCode: string) {
  if (soldToCode && getLocalOrderId(soldToCode)) {
    clearLocalOrderIds();
  }
}

export function otcSplitNameAddress(otc) {
  if (otc && Object.keys(otc).length > 0) {
    const splitObj = {
      name1: "",
      name2: "",
      street1: otc?.street1 || "",
      street2: "",
      street3: otc?.street2 || "",
      street4: "",
      district: ""
    };
    if (otc?.name1?.length <= 35) {
      splitObj.name1 = otc?.name1;
    } else {
      splitObj.name1 = otc?.name1?.substring(0, 35);
      splitObj.name2 = otc?.name1?.substring(35, 70);
    }
    if (splitObj.street1?.length > 40) {
      splitObj.street2 = splitObj.street1?.substring(40, 80);
      splitObj.street1 = splitObj.street1?.substring(0, 40);
    }
    if (splitObj.street3?.length > 40) {
      splitObj.street4 = splitObj.street3?.substring(40, 80);
      splitObj.street3 = splitObj.street3?.substring(0, 40);
    }
    splitObj.district = otc?.subDistrict + " " + otc?.district;
    delete otc.subDistrict;
    delete otc.paymentTerm;
    delete otc.taxClass;
    delete otc.salesEmployee;
    delete otc.soldToDisplayText;

    return { ...otc, ...splitObj };
  } else {
    return null;
  }
}

export const INIT_EMPTY_ORDERLINE = (itemNo: string): NotRefOrderLine => ({
  id: null,
  itemNo,
  bomFlag: false,
  parentId: null,
  parentItemNo: null,
  materialCode: null,
  materialDescription: null,
  customerMatCode: null,
  materialType: null,
  manualPriceFlag: false,
  quantity: null,
  salesUnit: null,
  weight: null,
  weightUnit: null,
  batchFlag: null,
  batchNo: null,
  batchChoiceFlag: null,
  plant: null,
  requestDate: null,
  pricePerUnit: null,
  netValue: null,
  priceCurrency: null,
  priceStatus: null,
  priceStatusDesc: null,
  paymentTermItem: null,
  priceDate: null,
  poDetails: null,
  poItemNumber: null,
  productionFlag: null,
  overDeliveryTol: null,
  underDeliveryTol: null,
  deliveryTolerance: null,
  shipTo: null,
  salesUnitList: [],
  internalCommentsToWarehouse: null,
  externalCommentsToCustomer: null,
  saleText_1: null,
  saleText_2: null,
  saleText_3: null,
  saleText_4: null,
  remark: null,
  itemNoteCip: null,
  prItemTextCip: null,
  lotNoPp: null,
  productionMemoPp: null,
  prcGroup1: null,
  isNewItem: null
});

export const customerNameMapping = (
  partners: OrderViewDataQuery["orderViewData"]["otcOrderPartners"],
  type: "AG"
): string => {
  if (!(partners?.length > 0)) {
    return "";
  }
  const otcSoldTo = partners
    .filter(partner => partner?.partnerRole === type)
    .map(partner => ({
      soldToCode: partner?.partnerNo || "",
      name: partner?.address[0]?.name || ""
    }));

  const name = otcSoldTo.length > 0 ? otcSoldTo[0].name : "";
  return name;
};

const mapDataAddress = (data, type) => {
  let address = "";

  if (data && type === "district") {
    if (data.includes("อ. ") || data.includes("เขต ")) {
      const regex = /อ\. .+|เขต .+/;
      address = data.match(regex)?.[0] || "";
    } else {
      address = data.split(" ")?.[1] || "";
    }
  }

  if (data && type === "subDistrict") {
    if (data.includes("ต. ") || data.includes("แขวง ")) {
      const regex = /(ต\. .+?)(?=\sอ\.|\sเขต|$)|(แขวง .+?)(?=\sอ\.|\sเขต|$)/;
      address = data.match(regex)?.[0] || "";
    } else {
      address = data.split(" ")?.[0] || "";
    }
  }

  return address.trim() || "";
};

export const orderPartnersToOtcMapping = (
  partners: OrderViewDataQuery["orderViewData"]["otcOrderPartners"],
  type: "AG" | "WE" | "RE"
): NotRefOTC | null => {
  if (!(partners?.length > 0)) {
    return null;
  }
  const result = partners
    .filter(partner => partner?.partnerRole === type)
    ?.map(partner => ({
      soldToCode: partner?.partnerNo || "",
      name1: partner?.address[0]?.name || "",
      city: partner?.address[0]?.city || "",
      postalCode: partner?.address[0]?.postCode || "",
      district: mapDataAddress(partner?.address[0]?.district, "district"),
      subDistrict: mapDataAddress(partner?.address[0]?.district, "subDistrict"),
      street1:
        (partner?.address[0]?.street || "") +
        (partner?.address[0]?.strSuppl1 || ""),
      street2:
        (partner?.address[0]?.strSuppl2 || "") +
        (partner?.address[0]?.strSuppl3 || ""),
      location: partner?.address[0]?.location || "",
      transportZoneCode: partner?.address[0]?.transpzone || "",
      transportZoneName: partner?.address[0]?.transpzone || "",
      countryCode: partner?.address[0]?.country || "",
      countryName: partner?.address[0]?.countryName || "", // check
      telephoneNo: partner?.address[0]?.telNoList[0]?.telNo || "",
      telephoneExtension: partner?.address[0]?.telNoList[0]?.telNoExt || "",
      mobileNo: partner?.address[0]?.mobileNo || "",
      faxNo: partner?.address[0]?.faxNo || "",
      faxNoExt: partner?.address[0]?.faxNoExt || "",
      email: "", // check
      language: partner?.address[0]?.language || "",
      branchId: partner?.address[0]?.orderTaxNumber?.branchId || "",
      taxId: partner?.address[0]?.orderTaxNumber?.taxId || "",
      taxNumber1: partner?.address[0]?.orderTaxNumber?.taxNumber1 || "",
      taxNumber2: partner?.address[0]?.orderTaxNumber?.taxNumber2 || ""
    }));
  if (result) {
    return result?.[0];
  } else {
    return null;
  }
};

export const OrderHeaderMapping = (
  order: GetOrderDataQuery["getOrderData"]
): NotRefOrderHeader => ({
  callPreDraft: false,
  id: order?.id || "",
  isOneTimeCustomer: !!order?.orderExtension?.otcSoldTo,
  salesOrganizationCode: order?.saleOrganization?.code || "",
  distributionChannelCode: order?.distributionChannel?.code || "",
  divisionCode: order?.division?.code || "00",
  salesOrganizationList: [],
  distributionChannelList: [],
  divisionList: [],
  orderType: order?.orderType || "",
  salesGroupCode: order?.salesGroup?.code || "",
  salesEmployee: order?.salesEmployee || "",
  salesOfficeCode: order?.saleOffice?.code || "",
  paymentTerm: order?.paymentTerm || "",
  taxClass: order?.orderExtension?.taxClass || "",
  poDate:
    order?.poDate || moment(new Date().toDateString()).format("YYYY-MM-DD"),
  requestDate: order?.requestDate || "",
  poNumber: order?.poNumber || "",
  customerId: order?.customer?.id || "",
  customerName: order?.customer?.name || "",
  soldToCode: order?.customer?.code || "",
  soldToDisplayText: order?.customer?.displayText || "",
  soldToAddress: order?.soldToAddress || "",
  billTo: order?.billTo || "",
  shipTo: order?.shipTo || "",
  unloadingPoint: order?.unloadingPoint || "",
  billTolist: [],
  shipTolist: [],
  unloadingPoints: [],
  cash: order?.orderExtension?.additionalTxtCash || "",
  headerNote1: order?.orderExtension?.additionalTxtHeaderNote1 || "",
  formHeader: order?.orderExtension?.additionalTxtFromHeader || "",
  externalCommentsToCustomer: order?.externalCommentsToCustomer || "",
  productInformation: order?.productInformation || "",
  internalCommentsToWarehouse: order?.internalCommentsToWarehouse || "",
  internalCommentsToLogistic: order?.internalCommentsToLogistic || "",

  otcSoldTo: order?.orderExtension?.otcSoldTo
    ? {
        ...otcMapping(order?.orderExtension?.otcSoldTo),
        salesEmployee: "",
        paymentTerm: order?.paymentTerm || "",
        taxClass: order?.orderExtension?.taxClass || ""
      }
    : null,
  otcShipTo: order?.orderExtension?.otcShipTo
    ? otcMapping(order?.orderExtension?.otcShipTo)
    : null,
  otcBillTo: order?.orderExtension?.otcBillTo
    ? otcMapping(order?.orderExtension?.otcBillTo)
    : null
});

export const changeOrderHeaderMapping = (
  order: OrderViewDataQuery["orderViewData"]
): NotRefOrderHeader => ({
  id: order?.id || "",
  isOneTimeCustomer: !!order?.oneTimeFlag,
  salesOrganizationCode: order?.salesOrgCode,
  salesOrganizationName: order?.salesOrgDisplayText,
  distributionChannelCode: order?.distributionChannelCode,
  distributionChannelName: order
    ? order?.distributionChannelCode + " - " + order?.distributionChannelName
    : "",
  divisionCode: order?.divisionCode,
  divisionCodeName: order
    ? order?.divisionCode + " - " + order?.divisionName
    : "",
  orderType: order?.orderType || "",
  orderTypeDisplayText: orderTypeMapping(order?.orderType) || "",
  salesGroupCode: order?.salesGroupCode || "",
  salesGroupCodeName: order
    ? order?.salesGroupCode + " - " + order?.salesGroupName
    : "",
  salesEmployee: order?.salesEmployeeDisplayText,
  salesOfficeCode: order?.salesOff || "",
  paymentTerm: order?.paymentTerms || "",
  paymentTermText: order?.paymentTermsDisplayText || "",
  taxClass: order?.taxClass || "",
  taxClassDisplayText: taxClassDescription(order?.taxClass) || "",
  poDate:
    order?.poDate || moment(new Date().toDateString()).format("YYYY-MM-DD"),
  requestDate: order?.requestDate || "",
  poNumber: order?.poNo || "",
  soldToCode: order?.soldToCode || "",
  soldToDisplayText: order?.soldToDisplayText || "",
  soldToAddress: "",
  billTo: order?.billToDisplayText || "",
  shipTo: order?.shipToDisplayText || "",
  unloadingPoint: order?.unloadingPoint || "",
  billTolist: [],
  shipTolist: [],
  unloadingPoints: [],
  cash: order?.cash || " - ",
  headerNote1: order?.headerNote1 || " - ",
  formHeader: order?.formHeader || " - ",
  externalCommentsToCustomer: order?.externalCommentsToCustomer || " - ",
  productInformation: order?.productionInformation || " - ",
  internalCommentsToWarehouse: order?.internalCommentsToWarehouse || " - ",
  internalCommentsToLogistic: order?.internalCommentsToLogistic || " - ",
  orderStatus: order?.status || "",
  otcSoldTo: !!order?.otcOrderPartners?.length
    ? {
        ...orderPartnersToOtcMapping(order?.otcOrderPartners, "AG"),
        salesEmployee: "",
        paymentTerm: order?.paymentTerms || "",
        taxClass: order?.taxClass || ""
      }
    : null,
  otcShipTo: orderPartnersToOtcMapping(order?.otcOrderPartners, "WE"),
  otcBillTo: orderPartnersToOtcMapping(order?.otcOrderPartners, "RE"),
  orderAmtBeforeVat: order?.orderAmtBeforeVat,
  orderAmtAfterVat: order?.orderAmtAfterVat,
  orderAmtVat: order?.orderAmtVat,
  currency: order?.currency,
  customerName: customerNameMapping(order?.otcOrderPartners, "AG"),
  prevReqDate: order?.requestDate || ""
});
const getNetPrice = line => {
  if (line?.pricePerUnit) {
    return +line.pricePerUnit * +line?.quantity;
  } else if (line?.price) {
    return +line.price * +line?.quantity;
  } else {
    return 0;
  }
};
const extractPoDeatils = (po, headerPo) => {
  if (po === headerPo) {
    return null;
  } else {
    return po ?? null;
  }
};

const getWeight = (weight, qty) => (qty ? weight : 0);

const getDefaultBatchNo = (
  line: Pick<
    NotRefOrderLine,
    "batchNo" | "batchFlag" | "bomFlag" | "parentItemNo"
  >
) => {
  if (line?.batchNo) {
    return line.batchNo;
  }
  if (line?.batchFlag && !(line?.bomFlag && !line?.parentItemNo)) {
    return null;
  }
  return "";
};

const getDefaultBatchChoiceFlag = (batchFlag: boolean, batchNo: string) => {
  if (!batchFlag || batchNo?.toUpperCase().trim() === BATCH_NO.NO_PRINT) {
    return BATCH_FLAG_CHOICES.BLANK;
  }
  return BATCH_FLAG_CHOICES.TEXT;
};

const commonLineMapping = (line: any, headerData: any): NotRefOrderLine => {
  const batchNo = getDefaultBatchNo(line);
  const batchChoiceFlag = getDefaultBatchChoiceFlag(line?.batchFlag, batchNo);
  return {
    id: line?.id || "",
    itemNo: line?.itemNo,
    bomFlag: line?.bomFlag,
    parentId: line?.parentId || "",
    parentItemNo: line?.parentItemNo || "",
    materialCode: line?.materialCode || "",
    materialDescription: line?.materialDescription || "",
    customerMatCode: line?.customerMatCode || "",
    materialType: line?.materialType || "",
    manualPriceFlag: line?.manualPriceFlag,
    quantity: line?.quantity || "",
    originalQuantity: +line?.quantity || 0,
    bomUnitQuantity: line?.bomUnitQuantity || 0,
    salesUnit: line?.salesUnit || "",
    weight: getWeight(line?.weight, line?.quantity),
    weightUnit: line?.weightUnit || "",
    batchFlag: line?.batchFlag || "",
    batchNo,
    batchChoiceFlag,
    plant: line?.plant || "",
    requestDate: line?.requestDate || "",
    pricePerUnit: line?.pricePerUnit || line?.price || 0,
    netValue: getNetPrice(line),
    priceCurrency: line?.priceCurrency || "",
    priceStatus: null,
    priceStatusDesc: null,
    paymentTermItem: line?.paymentTermItem || "",
    priceDate: line?.priceDate || "",
    poDetails: extractPoDeatils(line?.poNo, headerData?.poNumber),
    poItemNumber: line?.poItemNo || "",
    productionFlag: line?.productionFlag || "",
    overDeliveryTol: line?.overDeliveryTol || null,
    underDeliveryTol: line?.underDeliveryTol || null,
    deliveryTolerance: line?.overDeliveryTol || line?.underDeliveryTol || 0,
    shipTo: line?.shipTo || "",
    otcShipTo: line?.otcShipTo ? otcMapping(line?.otcShipTo) : null,
    salesUnitList: line?.salesUnitList || [],
    internalCommentsToWarehouse: line?.internalCommentsToWarehouse || "",
    externalCommentsToCustomer: line?.externalCommentsToCustomer || "",
    saleText_1: line?.saleText1 || "",
    saleText_2: line?.saleText2 || "",
    saleText_3: line?.saleText3 || "",
    saleText_4: line?.saleText4 || "",
    originalSaleText_1: line?.saleText1 || "",
    originalSaleText_2: line?.saleText2 || "",
    originalSaleText_3: line?.saleText3 || "",
    originalSaleText_4: line?.saleText4 || "",
    salesTextMaster: {
      saleText1: line?.salesTextMaster?.saleText1 || "",
      saleText2: line?.salesTextMaster?.saleText2 || "",
      saleText3: line?.salesTextMaster?.saleText3 || "",
      saleText4: line?.salesTextMaster?.saleText4 || ""
    },
    remark: line?.remark || "",
    itemNoteCip: line?.itemNote || "",
    prItemTextCip: line?.prItemText || "",
    lotNoPp: line?.lotNo || "",
    productionMemoPp: line?.productionMemo || "",
    prcGroup1: line?.prcGroup1,
    isNewItem: undefined,
    orderType: headerData?.orderType || ""
  };
};

export const orderLinesMapping = (
  lines: any[],
  headerData
): NotRefOrderLine[] => lines?.map(line => commonLineMapping(line, headerData));

export const changeOrderLinesMapping = (
  lines: OrderViewDataQuery["orderViewData"]["orderItems"],
  headerData: any
): ChangeNotRefOrderLine[] =>
  lines?.map(line => ({
    ...commonLineMapping(line, headerData),
    pricePerUnit: +line?.pricePerUnit,
    netValue: line?.netValue,
    paymentTermItem: line?.paymentTerm || " ",
    originalRequestDate: line?.originalRequestDate,
    deliveryQty: +line?.deliveryQty,
    deliveryStatus: line?.deliveryStatus,
    saleQtyDivision: line?.saleQtyDivision,
    saleQtyFactor: line?.saleQtyFactor,
    baseUnit: line?.baseUnit,
    reasonReject: line?.reasonReject,
    assignQuantity: line?.assignQuantity,
    itemStatusEn: line?.itemStatusEn,
    itemStatusTh: line?.itemStatusTh,
    prNoOs: line?.prNoOs, // purchaseNo
    prItemNoOs: line?.prItemOs, // prItem
    poNoOs: line?.poNoOs, // poSubcontract
    poItemNoOs: line?.poItemNoOs, // poItemNoOs
    poItemNumber: line?.poItemNumber, // poItemNo
    poGrStatusOs: line?.grStatusOs, // grStatus
    deliveryBlock: line?.deliveryBlock, // deliveryBlock
    shippingPoint: line?.shippingPoint, //
    isSplitEnabled: line?.isSplitEnabled,
    itemCategory: line?.itemCategory,
    confiremedDate: line?.confirmedDate,
    weight: line?.weight,
    prevReqDate: line?.requestDate
  }));

export const submitOrderLineMapping = (
  lines: NotRefOrderLine[],
  otherHeaderFields
): CipOrderLineInput[] => {
  const cpLines = [...lines];
  if (cpLines?.length === 1 && !cpLines[0]?.id) {
    return [];
  }
  if (cpLines?.length > 1) {
    if (!cpLines[0]?.id) {
      cpLines.shift();
    }
    if (!cpLines[cpLines.length - 1]?.id) {
      cpLines.pop();
    }
  }
  return cpLines.map(line => {
    const isChangeSaleText =
      line?.saleText_1 !== line?.originalSaleText_1 ||
      line?.saleText_2 !== line?.originalSaleText_2 ||
      line?.saleText_3 !== line?.originalSaleText_3 ||
      line?.saleText_4 !== line?.originalSaleText_4;

    return {
      batchNo: getBatchNo(line),
      bomFlag: line?.bomFlag,
      bomMaterialId: line?.parentId,
      deliveryTolUnlimited: null,
      externalCommentsToCustomer: line?.externalCommentsToCustomer,
      id: line?.id || "",
      internalCommentsToLogistic: "",
      internalCommentsToWarehouse: line?.internalCommentsToWarehouse,
      itemCategory: null,
      itemNo: line?.itemNo,
      itemNote: line?.itemNoteCip,
      lotNo: line?.lotNoPp,
      materialNo: line?.materialCode,
      deliveryTolOver: line?.overDeliveryTol,
      paymentTerm: line?.paymentTermItem,
      plant: line?.plant,
      poItemNo: line?.poItemNumber,
      poNo: line?.poDetails ?? otherHeaderFields.poNumber,
      poNoExternal: "",
      prItemText: line?.prItemTextCip,
      priceDate: line?.priceDate || null,
      pricePerUnit: +line?.pricePerUnit,
      productInformation: "",
      productionMemo: line?.productionMemoPp,
      quantity: +line?.quantity,
      remark: line?.remark,
      requestDate: line?.requestDate || null,
      requestDateChangeReason: "",
      route: "",
      ...(isChangeSaleText ? { saleText1: line?.saleText_1 } : {}),
      ...(isChangeSaleText ? { saleText2: line?.saleText_2 } : {}),
      ...(isChangeSaleText ? { saleText3: line?.saleText_3 } : {}),
      ...(isChangeSaleText ? { saleText4: line?.saleText_4 } : {}),
      shipTo: deriveShipToBillTo({
        v: line?.shipTo,
        otcV: line?.otcShipTo
      }),
      shippingPoint: "",
      deliveryTolUnder: line?.underDeliveryTol,
      unit: line?.salesUnit,
      weight: +line?.weight,
      weightUnit: line?.weightUnit,
      productionFlag: line?.productionFlag,
      manualPriceFlag: line?.manualPriceFlag
    };
  });
};

export const submitOrderInformationMapping = (
  info: NotRefOrderHeader
): CipOrderInformationInput => ({
  billTo: deriveShipToBillTo({ v: info?.billTo, otcV: { ...info?.otcBillTo } }),
  cash: info?.cash,
  customerGroup1Id: null,
  customerGroup2Id: null,
  customerGroup3Id: null,
  customerGroup4Id: null,
  customerId: info?.soldToCode || null,
  deliveryBlock: null,
  externalCommentsToCustomer: info?.externalCommentsToCustomer,
  fromHeader: info?.formHeader,
  headerNote1: info?.headerNote1,
  incotermsId: null,
  internalCommentsToLogistic: info?.internalCommentsToLogistic,
  internalCommentsToWarehouse: info?.internalCommentsToWarehouse,
  orderType: info?.orderType,
  paymentTerm: info?.paymentTerm,
  poDate: info?.poDate?.trim() || null,
  poNumber: info?.poNumber,
  productInformation: info?.productInformation || null,
  requestDate: info?.requestDate?.trim() || null,
  route: null,
  salesEmployee: info?.salesEmployee,
  shipTo: deriveShipToBillTo({ v: info?.shipTo, otcV: { ...info?.otcShipTo } }),
  shippingPoint: null,
  soldToCode: info?.soldToCode,
  taxClass: info?.taxClass,
  unloadingPoint: info?.unloadingPoint || "",
  otcBillTo: otcSplitNameAddress({ ...info?.otcBillTo }),
  otcShipTo: otcSplitNameAddress({ ...info?.otcShipTo }),
  otcSoldTo: otcSplitNameAddress({ ...info?.otcSoldTo })
});

export const isDirtyField = (formik, item) => {
  const value = formik.getFieldMeta(item).value;
  const initialValue = formik.getFieldMeta(item).initialValue;
  if (!initialValue && !value && !item.includes("poDetails")) {
    return false;
  }
  return String(initialValue) !== String(value);
};

const removeNullValues = obj =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null)
  );

const removeEmptyFields = obj =>
  Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => {
      const keepKeys = ["saleText1", "saleText2", "saleText3", "saleText4"];
      return keepKeys.includes(key) || (value !== null && value !== "");
    })
  );

export const removeEmptyObjects = arr =>
  arr?.filter(obj => Object.keys(obj).length !== 0);

export const submitChangeOrderHeaderMapping = (
  header: NotRefOrderHeader
): CipChangeOrderHeaderInput => ({
  cash: header?.cash ?? null,
  externalCommentsToCustomer: header?.externalCommentsToCustomer ?? null,
  fromHeader: header?.formHeader ?? null,
  headerNote1: header?.headerNote1 ?? null,
  internalCommentsToLogistic: header?.internalCommentsToLogistic ?? null,
  internalCommentsToWarehouse: header?.internalCommentsToWarehouse ?? null,
  paymentTerms: header?.paymentTerm ?? null,
  poNo: header?.poNumber ?? null,
  productInformation: header?.productInformation ?? null,
  requestDate: header?.requestDate ?? null,
  saleGroupCode: header?.salesGroupCode ?? null,
  salesEmployee: header?.salesEmployee ?? null,
  soNo: header?.soNo ?? null,
  taxClass: header?.taxClass ?? null,
  unLoadingPoint: header?.unloadingPoint ?? null,
  billTo: header?.billTo ?? null,
  shipTo: header?.shipTo ?? null,
  totalItems: header?.totalItems ?? null
});
const changeOrderHeaderField: Array<keyof NotRefOrderHeader> = [
  "cash",
  "externalCommentsToCustomer",
  "formHeader",
  "headerNote1",
  "internalCommentsToLogistic",
  "internalCommentsToWarehouse",
  "paymentTerm",
  "poNumber",
  "productInformation",
  "requestDate",
  "salesGroupCode",
  "salesEmployee",
  "soNo",
  "taxClass",
  "unloadingPoint",
  "billTo",
  "shipTo"
];

const changeOrderHeaderEditableField: Set<keyof NotRefOrderHeader> = new Set(
  changeOrderHeaderField
);

const changeOrderItemDetailsField: Array<keyof ChangeNotRefOrderLine> = [
  "batchNo",
  "overDeliveryTol",
  "underDeliveryTol",
  "externalCommentsToCustomer",
  "internalCommentsToWarehouse",
  "itemCategory",
  "itemNo",
  "itemNoteCip",
  "lotNoPp",
  "materialCode",
  "paymentTermItem",
  "plant",
  "poDetails",
  "poItemNumber",
  "prItemTextCip",
  "pricePerUnit",
  "priceDate",
  "productionMemoPp",
  "productionFlag",
  "quantity",
  "remark",
  "requestDate",
  "saleText_1",
  "saleText_2",
  "saleText_3",
  "saleText_4",
  "shipTo",
  "shippingPoint",
  "salesUnit",
  "saleQtyFactor"
];

const changeOrderItemDetailsEditableField: Set<keyof ChangeNotRefOrderLine> = new Set(
  changeOrderItemDetailsField
);

const findChangeOrderHeaderEditedField = (header, formik) => {
  const headerPayload = {};
  for (const key in formik.values) {
    if (changeOrderHeaderEditableField.has(key as keyof NotRefOrderHeader)) {
      if (isDirtyField(formik, key)) {
        headerPayload[key] = header[key];
      }
    }
  }
  return headerPayload;
};

export const prepareHeaderPayload = (
  header: NotRefOrderHeader,
  formik: any,
  orderId?: any,
  totalItems?: number
): CipChangeOrderHeaderInput => {
  const headerPayload = findChangeOrderHeaderEditedField(header, formik);

  const updatedPayload = {
    ...headerPayload,
    ...{
      soNo: orderId,
      totalItems
    }
  };
  const unfilteredData = submitChangeOrderHeaderMapping(updatedPayload);
  return removeNullValues(unfilteredData);
};

interface OptionalItemNoNotRefOrderLine
  extends Omit<NotRefOrderLine, "itemNo"> {
  itemNo?: string; // Override itemNo to be optional
}

const findChangeOrderItemDetailsEditedField = (line, formik, index) => {
  const linePayload: OptionalItemNoNotRefOrderLine = {};
  let isEdited = false;
  let prodFlagDirty = false;
  let saleTextHasChanged = false;
  for (const key in line) {
    if (line?.hasOwnProperty(key)) {
      if (
        changeOrderItemDetailsEditableField.has(
          key as keyof ChangeNotRefOrderLine
        )
      ) {
        if (isDirtyField(formik, `lines[${index}].${key}`)) {
          if (key === "productionFlag") {
            prodFlagDirty = true;
          }
          if (
            !saleTextHasChanged &&
            ["saleText_1", "saleText_2", "saleText_3", "saleText_4"].includes(
              key
            )
          ) {
            saleTextHasChanged = true;
          }

          isEdited = true;
          linePayload[key] = line[key];
        }
        if (key === "itemNoteCip" && prodFlagDirty) {
          linePayload[key] = line[key];
        }
      }
    }
  }

  if (saleTextHasChanged) {
    linePayload.saleText_1 = line?.saleText_1 || "";
    linePayload.saleText_2 = line?.saleText_2 || "";
    linePayload.saleText_3 = line?.saleText_3 || "";
    linePayload.saleText_4 = line?.saleText_4 || "";
  }
  return { linePayload, isEdited };
};

export const findChangeOrderCashSaleEditedField = (
  formik: FormikProps<ICashsaleFormValues>
) => {
  const payload: Partial<ICashsaleFormValues> = {};

  for (const key of Object.keys(formik.values) as Array<
    keyof ICashsaleFormValues
  >) {
    if (
      (["orderAmount", "arAmount", "customerPaymentDifference"] as Array<
        keyof ICashsaleFormValues
      >).includes(key)
    ) {
      continue;
    }

    if (key === "documentFiles") {
      const currentFiles = formik.values.documentFiles;
      const initialFiles = formik.initialValues.documentFiles;
      if (!isEqual(currentFiles, initialFiles)) {
        payload.documentFiles = formik.values.documentFiles;
      }
      continue;
    }

    if (isDirtyField(formik, key)) {
      (payload as any)[key] = formik.values[key];
    }
  }

  const isEdited = Object.keys(payload).length > 0;

  return { payload, isEdited };
};

export const checkHasPriceAffectingChanges = (
  formikItemDetails: FormikProps<{ lines: NotRefOrderLine[] }>
) => {
  const hasAddOrRemoveItems =
    formikItemDetails.values?.lines?.length !==
    formikItemDetails.initialValues?.lines?.length;
  if (hasAddOrRemoveItems) {
    return true;
  }

  const changedItemDetails = prepareItemDetailsPayload(
    formikItemDetails.values?.lines,
    formikItemDetails
  );

  const hasValuesChanged = changedItemDetails.some(item =>
    ["quantity", "unit"].some(key => key in item)
  );

  if (hasValuesChanged) {
    return true;
  }

  return false;
};

const keyMapping: { [key: string]: string } = {
  batchNo: "batchNo",
  batchChoiceFlag: "batchChoiceFlag",
  overDeliveryTol: "deliveryTolOver",
  underDeliveryTol: "deliveryTolUnder",
  externalCommentsToCustomer: "externalCommentsToCustomer",
  id: "id",
  internalCommentsToWarehouse: "internalCommentsToWarehouse",
  internalCommentsToLogistic: "internalCommentsToLogistic",
  itemCategory: "itemCategory",
  itemNo: "itemNo",
  itemNoteCip: "itemNote",
  lotNoPp: "lotNo",
  materialCode: "materialNo",
  paymentTermItem: "paymentTerm",
  plant: "plant",
  poDetails: "poDetail",
  poItemNumber: "poItemNo",
  prItemTextCip: "prItemText",
  pricePerUnit: "pricePerUnit",
  priceDate: "priceDate",
  productionMemoPp: "productionMemo",
  productionFlag: "productionFlag",
  quantity: "quantity",
  remark: "remark",
  requestDate: "requestDate",
  saleText_1: "saleText1",
  saleText_2: "saleText2",
  saleText_3: "saleText3",
  saleText_4: "saleText4",
  shipTo: "shipTo",
  shippingPoint: "shippingPoint",
  salesUnit: "unit",
  saleQtyFactor: "saleQtyFactor"
};

const getMapppedValue = (mappedKey, key, line) => {
  switch (mappedKey) {
    case "quantity":
    case "saleQtyFactor":
      return parseNumber(line[key], 0);
    default:
      return line[key];
  }
};

export const submitChangeOrderLineMapping = (
  lines: any[]
): CipChangeOrderEditInput["itemDetails"] =>
  lines.map(line => {
    const mappedLine: any = {};
    for (const key in line) {
      if (line.hasOwnProperty(key)) {
        const mappedKey = keyMapping[key] || key;
        mappedLine[mappedKey] = getMapppedValue(mappedKey, key, line);
      }
    }
    return mappedLine;
  });

export const prepareItemDetailsPayload = (
  lines: NotRefOrderLine[],
  formik: any
): CipChangeOrderItemDetailsInput[] => {
  const itemDetailsPayload = lines.map((line, index) => {
    const { linePayload, isEdited } = findChangeOrderItemDetailsEditedField(
      line,
      formik,
      index
    );
    if (isEdited) {
      return {
        id: line?.id || "",
        itemNo: line?.itemNo || "",
        materialCode: line?.materialCode || "",
        batchNo: getBatchNo(line),
        ...linePayload
      };
    }
  });
  const mappedChangeOrderLines = submitChangeOrderLineMapping(
    itemDetailsPayload
  );
  return mappedChangeOrderLines.filter(item => Object.keys(item).length > 0);
};

export const submitChangeOrderAddLineMapping = (
  lines: any[]
): CipChangeOrderEditInput["itemDetails"] =>
  lines.map(line => {
    const isChangeSaleText =
      line?.saleText_1 !== line?.originalSaleText_1 ||
      line?.saleText_2 !== line?.originalSaleText_2 ||
      line?.saleText_3 !== line?.originalSaleText_3 ||
      line?.saleText_4 !== line?.originalSaleText_4;

    return {
      id: line?.id ?? null,
      itemNo: line?.itemNo ?? null,
      materialNo: line?.materialCode ?? null,
      quantity: parseNumber(line?.quantity),
      unit: line?.salesUnit ?? null,
      batchNo: getBatchNo(line),
      saleQtyFactor: parseNumber(line?.saleQtyFactor, 0),
      plant: line?.plant ?? null,
      requestDate: line?.requestDate ?? null,
      priceDate: line?.priceDate ?? null,
      itemCategory: line?.itemCategory ?? null,
      paymentTerm: line?.paymentTermItem ?? null,
      poDetail: line?.poDetails ?? null,
      poItemNo: line?.poItemNumber ?? null,
      pricePerUnit: line?.pricePerUnit ?? null,
      shippingPoint: line?.shippingPoint ?? null,
      shipTo: deriveShipToBillTo({
        v: line?.shipTo,
        otcV: line?.otcShipTo
      }),
      productionFlag: line?.productionFlag ?? null,
      deliveryTolOver: line?.overDeliveryTol ?? null,
      deliveryTolUnder: line?.underDeliveryTol ?? null,
      externalCommentsToCustomer: line?.externalCommentsToCustomer ?? null,
      internalCommentsToWarehouse: line?.internalCommentsToWarehouse ?? null,
      internalCommentsToLogistic: line?.internalCommentsToLogistic ?? null,
      ...(isChangeSaleText ? { saleText1: line?.saleText_1 } : {}),
      ...(isChangeSaleText ? { saleText2: line?.saleText_2 } : {}),
      ...(isChangeSaleText ? { saleText3: line?.saleText_3 } : {}),
      ...(isChangeSaleText ? { saleText4: line?.saleText_4 } : {}),
      remark: line?.remark ?? null,
      itemNote: line?.itemNoteCip ?? null,
      prItemText: line?.prItemTextCip ?? null,
      lotNo: line?.lotNoPp ?? null,
      productionMemo: line?.productionMemoPp ?? null,
      manualPriceFlag: line?.manualPriceFlag
    };
  });

export const cipAddItemDetailsPayload = (
  lines: NotRefOrderLine[]
): CipChangeOrderItemDetailsInput[] => {
  if (lines?.length === 1 && !lines[0]?.id) {
    return [];
  }
  const newItems = lines?.filter(line => line.isNewItem === true);
  const unfilteredData = submitChangeOrderAddLineMapping(newItems);
  const filteredData = unfilteredData.map(removeEmptyFields);
  return filteredData.filter(item => Object.keys(item).length > 0);
};

export const submitOrderOrganizationMapping = (
  org: NotRefOrderHeader
): CipOrderOrganizationalDataInput => ({
  distributionChannelCode: org?.distributionChannelCode,
  divisionCode: org?.divisionCode,
  saleGroupCode: org?.salesGroupCode,
  saleOfficeCode: org?.salesOfficeCode,
  saleOrganizationCode: org?.salesOrganizationCode,
  salesEmployee: org?.salesEmployee
});

export const priceNotFoundMapping = (
  lines: NotRefOrderLine[]
): OrderLinePriceInfo[] => {
  if (lines?.length === 1 && !lines[0]?.id) {
    return [];
  }
  return lines.map(line => ({
    custMatCode: line?.customerMatCode || "",
    itemCategory: null,
    itemNo: line?.itemNo || "",
    materialNo: line?.materialCode || "",
    parentItemNo: line?.parentItemNo || "",
    plant: line?.plant || "",
    priceDate: line?.priceDate
      ? moment(line?.priceDate, [
          "DD/MM/YYYY",
          "YYYY/MM/DD",
          "DD-MM-YYYY"
        ]).format("YYYY-MM-DD")
      : "",
    quantity: line?.quantity || 0,
    requestDate: line?.requestDate || "",
    saleUnit: line?.salesUnit || "",
    weightUnit: line?.weightUnit || "",
    productionFlag: line?.productionFlag || undefined,
    batchNo: line?.batchNo || undefined,
    shipTo: line?.shipTo ? line?.shipTo : line?.otcShipTo?.soldToCode,
    rejectReason: line.itemStatusEn === ITEM_STATUS.CANCEL ? "93" : ""
  }));
};

export const priceNotFoundOrderInformationMapping = (
  info: NotRefOrderHeader,
  orderId?: any,
  isChangeFlow?: boolean
): OrderHeaderPriceInfo => ({
  billTo: info?.billTo ? info?.billTo : info?.otcBillTo?.soldToCode,
  currency: info?.currency,
  distributionChannel: info?.distributionChannelCode,
  division: info?.divisionCode,
  orderType: info?.orderType,
  paymentTerm: info?.paymentTerm,
  requestDate: info?.requestDate,
  salesOrganization: info?.salesOrganizationCode,
  shipTo: info?.shipTo ? info?.shipTo : info?.otcShipTo?.soldToCode,
  taxClassId: parseInt(info?.taxClass, 10),
  soNo: isChangeFlow ? orderId : undefined,
  salesEmployee: info?.salesEmployee
});

export const cpOrderLinesMapping = (lines: any[]): NotRefCPOrderLine[] =>
  lines.map(line => ({
    id: line?.id || "",
    itemNo: line?.itemNo,
    bomFlag: line?.bomFlag,
    parentItemNo: line?.parentItemNo || "",
    materialCode: line?.materialCode || "",
    materialDescription: line?.materialDescription || "",
    quantity: line?.quantity,
    requestDate: line?.requestDate || "",
    originalDate: line?.originalDate || "",
    confirmDate: line?.confirmDate || "",
    plant: line?.plant || "",
    showInPopup: line?.showInPopup
  }));

export const orderPriceLinesMapping = (lines: any[]): NotReforderPriceLine[] =>
  lines.map(line => ({
    itemNo: line?.itemNo,
    materialCode: line?.materialCode || "",
    materialDescription: line?.materialDescription || "",
    materialType: line?.materialType || "",
    manualPriceFlag: line?.manualPriceFlag || "",
    pricePerUnit: line?.pricePerUnit || "",
    netValue: line?.netValue,
    currency: line?.currency || "",
    priceStatus: line?.priceStatus || "",
    priceStatusDesc: line?.priceStatusDesc || ""
  }));

export const otcMapping = (
  otc: GetOrderDataQuery["getOrderData"]["orderExtension"]["otcSoldTo"]
): NotRefOTC => ({
  soldToCode: otc?.soldToCode || "",
  name1: (otc?.address?.name1 || "") + (otc?.address?.name2 || ""),
  city: otc?.address?.city || "",
  postalCode: otc?.address?.postalCode || "",
  district: mapDataAddress(otc?.address?.district, "district"),
  subDistrict: mapDataAddress(otc?.address?.district, "subDistrict"),
  street1: (otc?.address?.street1 || "") + (otc?.address?.street2 || ""),
  street2: (otc?.address?.street3 || "") + (otc?.address?.street4 || ""),
  location: otc?.address?.location || "",
  transportZoneCode: otc?.address?.transportZoneCode || "",
  transportZoneName: otc?.address?.transportZoneName || "",
  countryCode: otc?.address?.countryCode || "",
  countryName: otc?.address?.countryName || "",
  telephoneNo: otc?.address?.telephoneNo || "",
  telephoneExtension: otc?.address?.telephoneExtension || "",
  mobileNo: otc?.address?.mobileNo || "",
  faxNo: otc?.address?.faxNo || "",
  faxNoExt: otc?.address?.faxNoExt || "",
  email: otc?.address?.email || "",
  language: otc?.address?.language || "",
  branchId: otc?.address?.branchId || "",
  taxId: otc?.address?.taxId || "",
  taxNumber1: otc?.address?.taxNumber1 || "",
  taxNumber2: otc?.address?.taxNumber2 || ""
});

export const submitCPOrderLineMapping = (
  lines: NotRefCPOrderLine[],
  formikItemDetails: any
): any[] =>
  lines.map(line => ({
    itemNo: line?.itemNo || "",
    requestDate: line?.requestDate || null,
    manualPriceFlag: formikItemDetails?.values?.lines?.find(
      item => item.itemNo === line?.itemNo
    )?.manualPriceFlag,
    plant: line?.plant || ""
  }));

export const saveSplitItemOriginLineItemMapping = (
  lines: ChangeNotRefOrderLine[]
): SplitCipOrderLineInput[] =>
  lines.map(line => ({
    itemNo: line?.itemNo || "",
    id: +line?.id,
    quantity: line?.quantity,
    requestDate: moment(line?.requestDate, [
      "DD/MM/YYYY",
      "YYYY/MM/DD",
      "DD-MM-YYYY"
    ]).format("YYYY-MM-DD"),
    isParent: !line?.parentItemNo && line?.bomFlag === true
  }));

export const saveSplitItemLineMapping = (
  lines: ChangeNotRefOrderLine[]
): SplitCipOrderLineInput[] =>
  lines.map(line => {
    const isChangeSaleText =
      line?.saleText_1 !== line?.salesTextMaster?.saleText1 ||
      line?.saleText_2 !== line?.salesTextMaster?.saleText2 ||
      line?.saleText_3 !== line?.salesTextMaster?.saleText3 ||
      line?.saleText_4 !== line?.salesTextMaster?.saleText4;

    return {
      itemNo: line?.splitItemNo || "",
      quantity: line?.quantity,
      requestDate: moment(line?.requestDate, [
        "DD/MM/YYYY",
        "YYYY/MM/DD",
        "DD-MM-YYYY"
      ]).format("YYYY-MM-DD"),
      productionFlag:
        ProductionFlag[
          line?.productionFlag === "ผลิต" ? "PRODUCED" : "NOT_PRODUCED"
        ],
      originalItemId: +line?.id,
      isParent: !line?.parentItemNo && line?.bomFlag === true,
      internalCommentsToWarehouse: line?.internalCommentsToWarehouse,
      externalCommentsToCustomer: line?.externalCommentsToCustomer,
      ...(isChangeSaleText && {
        saleText1: line?.saleText_1,
        saleText2: line?.saleText_2,
        saleText3: line?.saleText_3,
        saleText4: line?.saleText_4
      }),
      remark: line?.remark,
      itemNoteCip: line?.itemNoteCip,
      prItemTextCip: line?.prItemTextCip,
      lotNo: line?.lotNoPp,
      productionMemoPp: line?.productionMemoPp
    };
  });

export const saveSplitItemOriginLineItemAfterCPMapping = (
  lines: ChangeNotRefOrderLine[]
): SplitCipOrderLineInputAfterCp[] =>
  lines.map(line => ({
    itemNo: line?.itemNo || "",
    id: +line?.id,
    quantity: line?.quantity,
    requestDate: moment(line?.requestDate, [
      "DD/MM/YYYY",
      "YYYY/MM/DD",
      "DD-MM-YYYY"
    ]).format("YYYY-MM-DD"),
    isParent: !line?.parentItemNo && line?.bomFlag === true,
    plant: line?.plant,
    confirmDate: line?.confirmDate,
    parentBom: line?.parentBom
  }));

export const saveSplitItemLineAfterCPMapping = (
  lines: ChangeNotRefOrderLine[]
): SplitCipOrderLineInputAfterCp[] =>
  lines.map(line => ({
    itemNo: line?.splitItemNo || "",
    quantity: line?.quantity,
    requestDate: moment(line?.requestDate, [
      "DD/MM/YYYY",
      "YYYY/MM/DD",
      "DD-MM-YYYY"
    ]).format("YYYY-MM-DD"),
    productionFlag:
      ProductionFlag[
        line?.productionFlag === "ผลิต" ? "PRODUCED" : "NOT_PRODUCED"
      ],
    originalItemId: +line?.id,
    isParent: !line?.parentItemNo && line?.bomFlag === true,
    internalCommentsToWarehouse: line?.internalCommentsToWarehouse,
    externalCommentsToCustomer: line?.externalCommentsToCustomer,
    saleText1: line?.saleText_1,
    saleText2: line?.saleText_2,
    saleText3: line?.saleText_3,
    saleText4: line?.saleText_4,
    remark: line?.remark,
    itemNoteCip: line?.itemNoteCip,
    prItemTextCip: line?.prItemTextCip,
    lotNo: line?.lotNoPp,
    productionMemoPp: line?.productionMemoPp,
    plant: line?.plant,
    confirmDate: line?.confirmDate,
    parentBom: line?.parentBom
  }));

const orderTypeMapping = orderType => {
  if (orderType === "ZP20") {
    return orderType + " - " + "Cash";
  } else if (orderType === "ZP30") {
    return orderType + " - " + "Credit";
  } else {
    return "";
  }
};

const taxClassDescription = taxClass => {
  if (taxClass === "1") {
    return "1 : Liable for Tax";
  } else if (taxClass === "0") {
    return "0 : Liable for taxes 0%";
  }
};

export const otcShipToMapping = otc => ({
  soldToCode: otc?.soldToCode,
  name1: otc?.name1 || "",
  street1: otc?.street1 || "",
  street2: otc?.street2 || "",
  countryCode: otc?.countryCode || "TH",
  countryName: otc?.countryName || "THAILAND",
  city: otc?.city || "",
  district: otc?.district || "",
  subDistrict: otc?.subDistrict || "",
  postalCode: otc?.postalCode || "",
  transportZoneCode: otc?.transportZoneCode || "",
  transportZoneName: otc?.transportZoneName || "",
  telephoneNo: otc?.telephoneNo || "",
  telephoneExtension: otc?.telephoneExtension || "",
  language: otc?.language || "",
  mobileNo: otc?.mobileNo || "",
  faxNo: otc?.faxNo || "",
  faxNoExt: otc?.faxNoExt || "",
  location: otc?.location || "",
  email: otc?.email || ""
});

const getBatchNo = (line: Pick<NotRefOrderLine, "batchFlag" | "batchNo">) => {
  if (!line?.batchFlag) {
    return "";
  }
  const isNoPrint = line?.batchNo?.toUpperCase().trim() === BATCH_NO.NO_PRINT;
  return isNoPrint ? BATCH_NO.NO_PRINT : line?.batchNo;
};

export const checkIsCashSaleOrder = (orderType: string) =>
  ["ZBV", "ZP20"].includes(orderType);

export const checkIsCashSaleAvailableForStatus = (
  orderStatus: string
): boolean =>
  ![ITEM_STATUS.COMPLETE_DELIVERY, ITEM_STATUS.CANCEL].includes(orderStatus);

export const getCashSaleStatusText = (
  statusName: string,
  hasError: boolean
) => {
  if (hasError) {
    return "Unable to Connect Cash Sale Status";
  }
  return statusName;
};

export const getCashSaleState = ({
  isChangeFlow,
  orderType,
  orderStatus,
  cashSaleStatusData
}: {
  isChangeFlow: boolean;
  orderType: string;
  orderStatus: string;
  cashSaleStatusData: GetCashSaleStatusQuery;
}) => {
  const isCashSaleOrder = checkIsCashSaleOrder(orderType);
  const isCashSaleAvailableForStatus = checkIsCashSaleAvailableForStatus(
    orderStatus
  );
  const canUpdateCashSale = !!cashSaleStatusData?.getCashSaleStatus?.updateFlag;

  let isEditableCashSale = false;
  let disabledActionForCashSale = false;

  if (isCashSaleOrder) {
    isEditableCashSale = isChangeFlow
      ? isCashSaleAvailableForStatus && canUpdateCashSale
      : true;

    disabledActionForCashSale = isChangeFlow
      ? isCashSaleAvailableForStatus && !canUpdateCashSale
      : false;
  }

  return {
    isCashSaleOrder,
    isCashSaleAvailableForStatus,
    isEditableCashSale,
    disabledActionForCashSale
  };
};

export const prepareCashSalePayload = (
  orderId: string,
  formik: FormikProps<ICashsaleFormValues>,
  inputOptions?: Partial<CashSaleInput>
) => {
  const variables:
    | SubmitCashSaleMutationVariables
    | SubmitCashSaleUpdateMutationVariables = {
    input: {
      orderId,
      orderAmount: Number(formik?.values?.orderAmount ?? 0) || 0,
      arAmount: Number(formik?.values?.arAmount ?? 0) || 0,
      cashFlag: !!formik?.values?.cashFlag,
      cashAmount: Number(formik?.values?.cashAmount ?? 0) || 0,
      balanceFlag: !!formik?.values?.balanceFlag,
      balanceAmount: Number(formik?.values?.balanceAmount ?? 0) || 0,
      feeAmount: Number(formik?.values?.fee ?? 0) || 0,
      feeType: formik?.values?.feeType,
      feeAbsorbFlag: !!formik?.values?.feeAbsorbFlag,
      withHoldTax: Number(formik?.values?.withHoldTax ?? 0) || 0,
      remark: formik?.values?.remark ?? "",
      ...(inputOptions ?? {})
    },
    files: formik?.values?.documentFiles
  };

  return { variables };
};
